import React, { useState } from 'react';
import {
  Box,
  Alert,
  Stack,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import GlowButton from './shared/GlowButton';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';

const UrlUtility: React.FC = () => {
  const theme = useTheme();
  const [input, setInput] = useState(() => localStorage.getItem('url-input') || '');
  const [output, setOutput] = useState(() => localStorage.getItem('url-output') || '');
  const [error, setError] = useState('');

  // Update localStorage when state changes
  React.useEffect(() => {
    localStorage.setItem('url-input', input);
  }, [input]);

  React.useEffect(() => {
    localStorage.setItem('url-output', output);
  }, [output]);

  const encodeUrl = () => {
    try {
      const encoded = encodeURIComponent(input);
      setOutput(encoded);
      setError('');
    } catch (err) {
      setError('Error encoding URL');
    }
  };

  const decodeUrl = () => {
    try {
      const decoded = decodeURIComponent(input);
      setOutput(decoded);
      setError('');
    } catch (err) {
      setError('Invalid URL encoded input');
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const swapInputOutput = () => {
    const temp = input;
    setInput(output);
    setOutput(temp);
    setError('');
  };

  return (
    <Box>
      <UtilityHeader 
        title="URL Tool" 
        description="Encode or decode URLs and URI components safely." 
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.3) }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextArea
            label="Input"
            rows={10}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste or type URL/text here..."
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextArea
            label="Output"
            accentColor="secondary"
            rows={10}
            value={output}
            onChange={(e) => setOutput(e.target.value)}
            placeholder="Result will appear here..."
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', gap: 2 }}>
        <GlowButton
          size="large"
          onClick={encodeUrl}
          disabled={!input.trim()}
          sx={{ px: 4 }}
        >
          URL Encode
        </GlowButton>
        <GlowButton
          size="large"
          color="secondary"
          glowColor="secondary"
          onClick={decodeUrl}
          disabled={!input.trim()}
          sx={{ px: 4 }}
        >
          URL Decode
        </GlowButton>
        <Box sx={{ flexGrow: 1 }} />
        <GlowButton
          variant="outlined"
          disableGlow
          onClick={swapInputOutput}
          disabled={!input.trim() && !output.trim()}
          startIcon={<SwapHoriz />}
          sx={{ color: 'text.primary', borderColor: 'rgba(255,255,255,0.2)' }}
        >
          Swap
        </GlowButton>
        <GlowButton
          variant="outlined"
          disableGlow
          color="inherit"
          onClick={clearAll}
          disabled={!input.trim() && !output.trim()}
          sx={{ opacity: 0.7, borderColor: 'rgba(255,255,255,0.2)' }}
        >
          Clear
        </GlowButton>
      </Stack>
    </Box>
  );
};

export default UrlUtility;
